import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {iso8601DateTimeZodValidator, uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {z as zod} from "zod";

export enum OttopilotAdditionalButtonKind {
    cancel = "3efb7232-1ad2-47b4-965f-5b7b03ba3d27",
    log = "c4d043ac-c0f9-4445-b39a-cf383aa76fa7",
}

/**
 * Semantic role of a row in {@link OttopilotAdditionalButtonsConfig["formColumnData"]}. Use {@link OttopilotAdditionalButtonFormFieldRole.cancelRemarks}
 * for the primary cancellation remarks field so generic modals can find it without a separate column id on the button config.
 */
export enum OttopilotAdditionalButtonFormFieldRole {
    default = "8e4f2c1a-3b7d-4e9f-8a2c-1d5e9f3b7a20",
    cancelRemarks = "9f5a3d2b-4c8e-4f0a-9b3d-2e6f0a4c8b31",
}

/** How a visible {@link OttopilotAdditionalButtonFormColumnData} field is rendered (text-like inputs). */
export enum OttopilotAdditionalButtonFormFieldPresentation {
    singleLineText = "c7d8e9f0-a1b2-4c3d-8e9f-0a1b2c3d4e5f",
    multiLineText = "d8e9f0a1-b2c3-4d5e-9f0a-1b2c3d4e5f6a",
}

/** Query keys a generic navigator may append when opening {@link OttopilotAdditionalButtonsConfig.formUrl}. */
export type OttopilotAdditionalButtonFormUrlQueryParam = "eventContainerId" | "eventId" | "workspaceId";

export enum OttopilotRunTimeValueKind {
    static = "c6a52f92-ea97-4dd8-9f2a-5240b2213de1",
    userTemplate = "63370c03-fe6c-4b27-8cf5-7010c1cbd5c1",
    user = "12345678-9012-3456-7890-123456789012",
    duration = "7296e4e0-5d37-4ae6-97bb-4b88be114f0d",
    timestamp = "98765432-1098-7654-3210-987654321098",
}

export enum OttopilotOperator {
    equals = "7444c031-7f10-49d5-9d1e-07608301b580",
    notEquals = "6b596630-305d-411b-8d6f-664881879d1b",
    contains = "14864215-8824-4d74-a764-075816208d1c",
    notContains = "89470912-931f-473c-8114-574631b50744",
    isEmpty = "33565618-10d0-406e-9083-086527a0723c",
    isNotEmpty = "55228457-6d2c-4721-a597-795c1665928c",
    isNull = "86771670-54a8-453b-894b-969b0676073c",
    isNotNull = "99176521-812c-439c-8537-10147196014c",
}

/**
 * One field in a modal or inline form opened from an additional button. {@link OttopilotAdditionalButtonsConfig.formColumnData}
 *
 * `kind` describes how a **default** is resolved for hidden or prefilled fields (not visibility-rule {@link OttopilotRunTimeValueKind}).
 */
export type OttopilotAdditionalButtonFormColumnData = {
    columnId: Uuid;
    kind: OttopilotRunTimeValueKind;
    columnType: "OttopilotEvent" | "OttopilotEventContainer";
    /** Defaults to {@link OttopilotAdditionalButtonFormFieldRole.default}. */
    fieldRole?: OttopilotAdditionalButtonFormFieldRole;
    label?: string;
    isVisible?: boolean;
    isRequired?: boolean;
    placeHolder?: string;
    defaultValue?: string | Uuid | Iso8601DateTime | number | boolean | null;
    /** Defaults to {@link OttopilotAdditionalButtonFormFieldPresentation.singleLineText} for visible text-like fields. */
    presentation?: OttopilotAdditionalButtonFormFieldPresentation;
    /** When true, show value but do not allow editing. */
    readOnly?: boolean;
    /** Lower sorts first when building the form. */
    sortOrder?: number;
};

/** One condition inside {@link OttopilotAdditionalButtonVisibilityRule}. */
export type OttopilotAdditionalButtonRuleCondition = {
    attribute: string;
    operator: OttopilotOperator;
} & (
    | {
          kind: OttopilotRunTimeValueKind.static;
          value: string;
      }
    | {
          kind: OttopilotRunTimeValueKind.userTemplate;
          value: Uuid;
      }
    | {
          kind: OttopilotRunTimeValueKind.duration;
          value: number;
      }
);

/**
 * One rule group for {@link OttopilotAdditionalButtonsConfig}. Conditions combine per {@link OttopilotAdditionalButtonVisibilityRule.logicalOperator}.
 */
export type OttopilotAdditionalButtonVisibilityRule = {
    conditions: Array<OttopilotAdditionalButtonRuleCondition>;
    logicalOperator: "and" | "or";
};

export type OttopilotAdditionalButtonsConfig = {
    /** Stable identifier for this button configuration. */
    id: Uuid;
    /** Text shown on the button in the UI. */
    label: string;
    /** Which behavior the host associates with this entry (navigation vs modal vs inline form). */
    buttonKind: OttopilotAdditionalButtonKind;
    /** Whether the button opens a modal dialog instead of a form. */
    showModal?: boolean;
    /** Optional description of the button's purpose shown in the UI when button is pressed. */
    description?: string;
    /**
     * Visibility or enablement rules for this additional button. Each array element is one rule. Within that
     * rule, `logicalOperator` defines how `conditions` combine: `"and"` requires every condition to match
     * for the rule to pass; `"or"` requires at least one condition to match. How multiple rules in the array
     * combine with each other is defined where the rules are evaluated.
     *
     * Each condition compares an entity `attribute` (field path or identifier understood by the evaluator)
     * using {@link OttopilotOperator} against a right-hand side whose shape is fixed by {@link OttopilotRunTimeValueKind}:
     * - `static` — literal string compared as configured by the operator.
     * - `userTemplate` — compares against the signed-in user; `value` is typically a user-template or role id
     *   used by the backend or client evaluator.
     * - `duration` — numeric duration in milliseconds for time-based operators.
     */
    rules: Array<OttopilotAdditionalButtonVisibilityRule>;
    /**
     * Fields for a modal or inline collection flow: bind to Event or Event Container columns, control visibility with
     * `isVisible`, and mark the cancellation remarks field with {@link OttopilotAdditionalButtonFormFieldRole.cancelRemarks}.
     */
    formColumnData?: Array<OttopilotAdditionalButtonFormColumnData>;
    /**
     * In-app route template (may contain `$workspaceId`). When set, the host may navigate instead of opening
     * `formColumnData` in a modal.
     */
    formUrl?: string;
    /**
     * Query parameter names to append when navigating to `formUrl` (values supplied at runtime by the host).
     */
    formUrlQueryParams?: ReadonlyArray<OttopilotAdditionalButtonFormUrlQueryParam>;
};

const additionalButtonDefaultValueValidator = zod.union([uuidZodValidator, iso8601DateTimeZodValidator, zod.string(), zod.number(), zod.boolean(), zod.null()]);

const additionalButtonFormColumnDataValidator: zod.ZodType<OttopilotAdditionalButtonFormColumnData> = zod.strictObject({
    columnId: uuidZodValidator,
    kind: zod.enum(OttopilotRunTimeValueKind),
    columnType: zod.enum(["OttopilotEvent", "OttopilotEventContainer"]),
    fieldRole: zod.enum(OttopilotAdditionalButtonFormFieldRole).optional(),
    label: zod.string().optional(),
    isVisible: zod.boolean().optional(),
    isRequired: zod.boolean().optional(),
    placeHolder: zod.string().optional(),
    defaultValue: additionalButtonDefaultValueValidator.optional(),
    presentation: zod.enum(OttopilotAdditionalButtonFormFieldPresentation).optional(),
    readOnly: zod.boolean().optional(),
    sortOrder: zod.number().optional(),
});

const additionalButtonRuleConditionValidator: zod.ZodType<OttopilotAdditionalButtonRuleCondition> = zod.discriminatedUnion("kind", [
    zod.strictObject({
        attribute: zod.string(),
        operator: zod.enum(OttopilotOperator),
        kind: zod.literal(OttopilotRunTimeValueKind.static),
        value: zod.string(),
    }),
    zod.strictObject({
        attribute: zod.string(),
        operator: zod.enum(OttopilotOperator),
        kind: zod.literal(OttopilotRunTimeValueKind.userTemplate),
        value: uuidZodValidator,
    }),
    zod.strictObject({
        attribute: zod.string(),
        operator: zod.enum(OttopilotOperator),
        kind: zod.literal(OttopilotRunTimeValueKind.duration),
        value: zod.number(),
    }),
]);

const additionalButtonVisibilityRuleValidator: zod.ZodType<OttopilotAdditionalButtonVisibilityRule> = zod.strictObject({
    conditions: additionalButtonRuleConditionValidator.array(),
    logicalOperator: zod.enum(["and", "or"]),
});

export const ottopilotAdditionalButtonsConfigValidator: zod.ZodType<OttopilotAdditionalButtonsConfig> = zod.strictObject({
    id: uuidZodValidator,
    label: zod.string(),
    buttonKind: zod.enum(OttopilotAdditionalButtonKind),
    showModal: zod.boolean().optional(),
    description: zod.string().optional(),
    rules: additionalButtonVisibilityRuleValidator.array(),
    formColumnData: additionalButtonFormColumnDataValidator.array().optional(),
    formUrl: zod.string().optional(),
    formUrlQueryParams: zod.enum(["eventContainerId", "eventId", "workspaceId"]).array().readonly().optional(),
});
